import { useEffect, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import Cookies from "js-cookie";
import { COOKIE_NAMES, COOKIE_EXPIRY_DAYS } from "@/lib/constants/cookies";
import { AppNavbar } from "@/components/AppNavbar";
import { AppSidebar } from "@/components/AppSidebar";
import { getSidebarState } from "@/lib/readSidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useUser } from "@/hooks/useUser";
import { PATHS } from "@/config/paths";
import { authDialog } from "@/state/dialogs";
import { getShortcut } from "@/config/shortcuts";
import { useCommandMenu } from "@/contexts/CommandMenuContext";
import { useChatFocus } from "@/contexts/ChatFocusContext";
import { isTypingInInput } from "@/utilities/input";

export const Route = createFileRoute("/_dashboard")({
  component: HomeLayout,
  loader: async () => {
    // Get initial sidebar state from server
    const sidebarOpen = await getSidebarState();
    return { sidebarOpen };
  },
});

function HomeLayout() {
  const { sidebarOpen } = Route.useLoaderData();
  const navigate = useNavigate();
  const { openCommandMenu } = useCommandMenu();
  const { focusChatInput } = useChatFocus();
  const { isAuthenticated } = useUser();
  const setAuthDialogState = useSetAtom(authDialog);
  const [sidebarOpenState, setSidebarOpenState] = useState(sidebarOpen);
  const [showSidebarTitle, setShowSidebarTitle] = useState(sidebarOpen);
  const showTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSidebarChange = (open: boolean) => {
    // Clear any pending timeout
    if (showTitleTimeoutRef.current) {
      clearTimeout(showTitleTimeoutRef.current);
      showTitleTimeoutRef.current = null;
    }

    // Hide title immediately when closing starts
    if (!open) {
      setShowSidebarTitle(false);
    }
    setSidebarOpenState(open);
    Cookies.set(COOKIE_NAMES.SIDEBAR_STATE, String(open), { expires: COOKIE_EXPIRY_DAYS });
    // Show title after sidebar opening transition completes (100ms)
    if (open) {
      showTitleTimeoutRef.current = setTimeout(() => {
        setShowSidebarTitle(true);
        showTitleTimeoutRef.current = null;
      }, 100);
    }
  };

  const handleNewChat = () => {
    // Navigate to home route for new chat creation
    navigate({ to: "/" });
    // Focus the chat input after navigation
    setTimeout(() => {
      focusChatInput();
    }, 100);
  };

  const handleSettingsClick = () => {
    if (isAuthenticated) {
      navigate({ to: PATHS.SETTINGS });
    } else {
      setAuthDialogState({ open: true });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (showTitleTimeoutRef.current) {
        clearTimeout(showTitleTimeoutRef.current);
      }
    };
  }, []);

  // Handle sidebar toggle shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle 'b' shortcut to toggle sidebar
      if (
        e.key === getShortcut("toggleSidebar").key &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTypingInInput(e.target)
      ) {
        e.preventDefault();
        handleSidebarChange(!sidebarOpenState);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [sidebarOpenState]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex h-screen">
        <SidebarProvider
          open={sidebarOpenState}
          onOpenChange={handleSidebarChange}
          collapsible="icon"
        >
          <AppSidebar showTitle={showSidebarTitle} />
          <SidebarInset>
            <div className="flex-1 bg-background relative">
              <AppNavbar
                onSearchClick={openCommandMenu}
                onNewChatClick={handleNewChat}
                onSettingsClick={handleSettingsClick}
              />
              <main className="flex flex-col h-screen">
                <Outlet />
              </main>
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </div>
  );
}
